import {
  Box,
  Column,
  Columns,
  IconChevron,
  Link,
  Stack,
  Text,
} from 'braid-design-system';
import type { ComponentProps } from 'react';
import { useLocation } from 'react-router';
import { InlineCode } from 'scoobie';

import type { WebhookSubscriptionsQuery } from '#src/types/generated/graphql.ts';

import { SubscriptionDeliverabilityIcon } from './components/SubscriptionDeliverabilityIcon/SubscriptionDeliverabilityIcon.tsx';

import * as styles from './styles.css';

type QueriedSubscription =
  WebhookSubscriptionsQuery['webhookSubscriptions']['edges'][number]['node'];

interface TitleTextProps {
  text: string;
  size: ComponentProps<typeof Text>['size'];
}

const determineTitleText = (
  subscription: QueriedSubscription,
): TitleTextProps => {
  if (!subscription.hirerId) {
    return { size: 'small', text: subscription.url };
  }

  if (!subscription.hirer) {
    // If there's a hirer ID but no hirer, fall back to ID to show relationship.
    return { size: 'standard', text: subscription.hirerId.value };
  }

  return { size: 'standard', text: subscription.hirer.name };
};

interface WebhookSubscriptionCardProps {
  subscription: QueriedSubscription;
}

export const WebhookSubscriptionCard = ({
  subscription,
}: WebhookSubscriptionCardProps) => {
  const location = useLocation();

  const pathname = `/manage/webhooks/${encodeURIComponent(
    subscription.id.value,
  )}`;

  const titleText = determineTitleText(subscription);

  return (
    <Link
      className={{
        [styles.selectedCardLink]: location.pathname === pathname,
      }}
      href={pathname}
    >
      <Box
        className={styles.card}
        cursor="pointer"
        padding="small"
        overflow="hidden"
      >
        <Columns space="small">
          <Column>
            <Stack space="medium">
              <Text size={titleText.size} maxLines={5}>
                <Box component="span" style={{ overflowWrap: 'anywhere' }}>
                  {titleText.text}
                </Box>
              </Text>

              <Text
                icon={
                  <SubscriptionDeliverabilityIcon
                    subscriptionId={subscription.id.value}
                    createdAt={subscription.createDateTime}
                    recentRequests={subscription.webhookRequests.edges.map(
                      (edge) => edge.node,
                    )}
                  />
                }
                maxLines={1}
                size="small"
              >
                <InlineCode>{subscription.eventTypeCode}</InlineCode>
              </Text>
            </Stack>
          </Column>

          <Column width="content">
            <Box className={styles.selectedChevron}>
              <IconChevron direction="right" size="xsmall" tone="secondary" />
            </Box>
          </Column>
        </Columns>
      </Box>
    </Link>
  );
};
